<template>
  <div class="card">
    <Toast />
    <div class="title">Daftar Pembelian</div>

    <Toolbar class="p-mb-2" v-if="user && user.role == 'MAIN_PURCHASING'">
      <template slot="left">
        <Button
          label="Tambah"
          icon="pi pi-plus"
          class="p-button-success"
          @click="create_dialog = true"
        />
      </template>
      <template slot="right"> </template>
    </Toolbar>

    <DataTable
      :value="initial_purchase_orders"
      :paginator="true"
      :lazy="true"
      class="p-datatable-sm"
      :rows="limit"
      data-key="id"
      :totalRecords="total_records"
      :loading="loading.filter"
      @page="getInitialPurchaseOrders($event)"
      :scrollable="true"
      scrollHeight="550px"
    >
      <template #header>
        <div class="p-grid p-fluid">
          <div class="p-col-12 p-md-3">
            <label for="mutation-code" class="block mb-1">Kode Mutasi</label>
            <InputText
              id="mutation-code"
              type="text"
              v-model="filter.mutation_code"
              placeholder="Cari kode mutasi..."
              @input="is_error.mutation_code = false"
              class="w-100"
            />
            <small class="p-error" v-if="is_error.mutation_code">{{
              error_msg.mutation_code
            }}</small>
          </div>

          <div class="p-col-12 p-md-3">
            <label for="doc-date" class="block mb-1">Tanggal Bon</label>
            <Calendar
              id="doc-date"
              v-model="filter.doc_date"
              :showIcon="true"
              dateFormat="dd/mm/yy"
              @date-select="is_error.doc_date = false"
              class="w-100"
            />
            <small class="p-error" v-if="is_error.doc_date">{{
              error_msg.doc_date
            }}</small>
          </div>

          <div class="p-col-12 p-md-3">
            <label for="supplier" class="block mb-1">Supplier</label>
            <span class="p-input-icon-right w-100" style="display: block">
              <i
                v-if="loading.suppliers"
                class="pi pi-spin pi-spinner"
                style="z-index: 10"
              ></i>
              <AutoComplete
                id="supplier"
                v-model="supplier_selected"
                :suggestions="autoFilteredValue"
                field="alias"
                @complete="searchSupplier($event)"
                :dropdown="true"
                placeholder="Cari supplier..."
                class="w-100"
              >
                <template #item="slotProps">
                  <div class="warehouse-item">
                    <span class="warehouse-text">{{
                      slotProps.item.alias
                    }}</span>
                  </div>
                </template>
              </AutoComplete>
            </span>
          </div>

          <div class="p-col-12 p-md-3">
            <label for="doc-number" class="block mb-1">Nomor Bon</label>
            <InputText
              id="doc-number"
              type="text"
              v-model="filter.doc_number"
              placeholder="Cari nomor bon..."
              @input="is_error.doc_number = false"
              class="w-100"
            />
            <small class="p-error" v-if="is_error.doc_number">{{
              error_msg.doc_number
            }}</small>
          </div>

          <div class="p-col-12 p-md-3 p-lg-3 p-d-flex p-ai-end">
            <Button
              label="Cari"
              icon="pi pi-search"
              @click="getInitialPurchaseOrders()"
              :loading="loading.filter"
              class="p-mr-2"
            />
            <Button
              label="Reset"
              icon="pi pi-refresh"
              @click="resetFilters()"
              class="p-button-secondary"
              v-tooltip.top="'Reset Filter'"
            />
          </div>
        </div>
      </template>

      <template #empty>
        <div class="empty-state">
          <i class="pi pi-inbox mb-3" style="font-size: 2rem"></i>
          <p>Data tidak tersedia</p>
        </div>
      </template>

      <Column
        field="no"
        header="No."
        :headerStyle="{ width: '2.5rem', minWidth: '2.5rem' }"
        :bodyStyle="{ width: '2.5rem', minWidth: '2.5rem' }"
      >
        <template #body="slotProps">
          {{ (currentPage - 1) * limit + slotProps.index + 1 }}
        </template>
      </Column>

      <Column
        v-if="user && user.role == 'MAIN_PURCHASING'"
        field="warehouse_name"
        header="Warehouse"
        :headerStyle="{ width: '12.5rem', minWidth: '12.5rem' }"
        :bodyStyle="{ width: '12.5rem', minWidth: '12.5rem' }"
      >
        <template #body="slotProps">
          <span
            class="text-truncate"
            :title="`${slotProps.data.warehouse_name || ''}`"
          >
            <span>{{ truncateText(slotProps.data.warehouse_name, 20) }}</span>
          </span>
        </template>
      </Column>

      <Column
        field="mutation_code"
        header="Kode Mutasi"
        :headerStyle="{ width: '10rem', minWidth: '10rem' }"
        :bodyStyle="{ width: '10rem', minWidth: '10rem' }"
      >
        <template #body="slotProps">
          <span class="font-medium">{{
            slotProps.data.mutation_code || '-'
          }}</span>
        </template>
      </Column>

      <Column
        field="doc_date"
        header="Tanggal Bon"
        :headerStyle="{ width: '7rem', minWidth: '7rem' }"
        :bodyStyle="{ width: '7rem', minWidth: '7rem' }"
      >
        <template #body="slotProps">
          <span>{{ slotProps.data.doc_date }}</span>
        </template>
      </Column>

      <Column
        field="doc_number"
        header="No Bon"
        :headerStyle="{ width: '13.5rem', minWidth: '13.5rem' }"
        :bodyStyle="{ width: '13.5rem', minWidth: '13.5rem' }"
      >
        <template #body="slotProps">
          <span class="font-medium">{{ slotProps.data.doc_number }}</span>
        </template>
      </Column>

      <Column
        field="supplier_name"
        header="Supplier"
        :headerStyle="{ width: '12.5rem', minWidth: '12.5rem' }"
        :bodyStyle="{ width: '12.5rem', minWidth: '12.5rem' }"
      >
        <template #body="slotProps">
          <span
            class="text-truncate"
            :title="
              slotProps.data.supplier_code && slotProps.data.supplier_name
                ? `${slotProps.data.supplier_code} - ${slotProps.data.supplier_name}`
                : ''
            "
          >
            <template
              v-if="
                slotProps.data.supplier_code && slotProps.data.supplier_name
              "
            >
              <span class="font-medium">{{
                slotProps.data.supplier_code
              }}</span>
              <span class="mx-2"> - </span>
              <span>{{ truncateText(slotProps.data.supplier_name, 20) }}</span>
            </template>
            <span v-else>-</span>
          </span>
        </template>
      </Column>

      <Column
        field="due_date"
        header="Jatuh Tempo"
        :headerStyle="{ width: '7rem', minWidth: '7rem' }"
        :bodyStyle="{ width: '7rem', minWidth: '7rem' }"
      >
        <template #body="slotProps">
          <span>{{ slotProps.data.due_date }}</span>
        </template>
      </Column>

      <Column
        field="status"
        header="Status"
        :headerStyle="{ width: '5rem', minWidth: '5rem' }"
        :bodyStyle="{ width: '5rem', minWidth: '5rem' }"
      >
        <template #body="slotProps">
          <span
            :class="[
              'status-badge',
              `status-${slotProps.data.status === 1 ? 'success' : 'warning'}`
            ]"
          >
            {{ checkStatus(slotProps.data.status) }}
          </span>
        </template>
      </Column>

      <Column
        field="total_item"
        header="Total Barang"
        :headerStyle="{ width: '5rem', minWidth: '5rem' }"
        :bodyStyle="{ width: '5rem', minWidth: '5rem' }"
      >
        <template #body="slotProps">
          <span class="text-right">{{ slotProps.data.total_item }}</span>
        </template>
      </Column>

      <Column
        field="total_price"
        header="Total Harga"
        :headerStyle="{ width: '8rem', minWidth: '8rem' }"
        :bodyStyle="{ width: '8rem', minWidth: '8rem' }"
      >
        <template #body="slotProps">
          <span class="text-right font-medium">{{
            slotProps.data.total_price | currency
          }}</span>
        </template>
      </Column>

      <Column
        :headerStyle="{ width: '5rem', minWidth: '5rem' }"
        :bodyStyle="{ width: '5rem', minWidth: '5rem' }"
      >
        <template #body="slotProps">
          <router-link
            :to="{
              name: 'purchase-edit',
              query: {
                id: slotProps.data.id
              }
            }"
          >
            <Button
              v-if="
                slotProps.data.status === 0 && user && user.role == 'PURCHASING'
              "
              icon="pi pi-pencil"
              class="p-button-rounded p-button-success p-mr-2"
            >
            </Button>
          </router-link>

          <router-link
            :to="{
              name: 'purchase-edit',
              query: {
                id: slotProps.data.id,
                doc_number: slotProps.data.doc_number
              }
            }"
          >
            <Button
              v-if="slotProps.data.status === 1"
              type="button"
              icon="pi pi-eye"
              class="p-button-rounded p-mr-2"
            ></Button>
          </router-link>
          <Button
            v-if="
              slotProps.data.status === 1 &&
              user &&
              user.role === 'WAREHOUSE_MANAGER'
            "
            type="button"
            icon="pi pi-pencil"
            class="p-button-rounded p-button-success p-mr-2"
            @click="warehouseManagerDialog(slotProps.data)"
          ></Button>
          <Button
            v-if="
              user &&
              user.role == 'MAIN_PURCHASING' &&
              slotProps.data.status != 1
            "
            icon="pi pi-trash"
            class="p-button-rounded p-button-warning"
            @click="confirmDelete(slotProps.data)"
          ></Button>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :visible.sync="delete_dialog"
      :style="{ width: '450px' }"
      header="Konfirmasi Hapus"
      :modal="true"
    >
      <div class="confirmation-content">
        Apakah kamu yakin ingin menghapus
        <b>{{ delete_data.doc_number }}</b
        >?
      </div>

      <template #footer>
        <Button
          label="Tidak"
          icon="pi pi-times"
          class="p-button-text"
          @click="delete_dialog = false"
        />
        <Button
          label="Ya"
          icon="pi pi-check"
          class="p-button-text"
          @click="deleteInitialPurchaseOrders()"
        />
      </template>
    </Dialog>

    <Dialog
      :visible.sync="warehouse_manager_dialog"
      :style="{ width: '450px' }"
      header="Konfirmasi Ubah Status"
      :modal="true"
    >
      <div class="confirmation-content">
        Apakah kamu yakin ingin mengubah status ?
      </div>

      <template #footer>
        <Button
          label="Tidak"
          icon="pi pi-times"
          class="p-button-text"
          @click="warehouse_manager_dialog = false"
        />
        <Button
          label="Ya"
          icon="pi pi-check"
          class="p-button-text"
          @click="warehouseManagerEdit(initial_purchase_id)"
        />
      </template>
    </Dialog>

    <Dialog
      :visible.sync="create_dialog"
      :style="{ width: '450px' }"
      header="Tambah Pembelian"
      :modal="true"
      class="p-fluid"
    >
      <div class="p-field">
        <label for="">Warehouse</label>
        <Dropdown
          v-model="warehouse_selected"
          :options="warehouses"
          optionLabel="name"
          placeholder="Pilih Warehouse"
          @change="is_error.warehouse_id = false"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value">
              {{ slotProps.value.code }} - {{ slotProps.value.name }}
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div>{{ slotProps.option.code }} - {{ slotProps.option.name }}</div>
          </template>
        </Dropdown>
        <small class="p-error" v-if="is_error.warehouse_id">{{
          error_msg.warehouse_id
        }}</small>
      </div>
      <div class="p-field">
        <label for="">Supplier</label>
        <AutoComplete
          placeholder="Supplier"
          :dropdown="true"
          v-model="supplier_selected"
          :suggestions="autoFilteredValue"
          @complete="searchSupplier"
          field="alias"
        >
          <template #item="slotProps">
            <div>{{ slotProps.item.alias }}</div>
          </template>
        </AutoComplete>
        <small class="p-error" v-if="is_error.sup_id">{{
          error_msg.sup_id
        }}</small>
      </div>
      <div class="p-field">
        <label for="">Nomor Bon</label>
        <div>
          <InputText
            v-model="create_data.doc_number"
            placeholder="Nomor Bon"
            @input="is_error.doc_number = false"
          />
        </div>
        <small class="p-error" v-if="is_error.doc_number">{{
          error_msg.doc_number
        }}</small>
      </div>
      <div class="p-field">
        <label for="">Tanggal Bon</label>
        <InputText
          type="date"
          v-model="create_data.doc_date"
          @input="is_error.doc_date = false"
        />
        <small class="p-error" v-if="is_error.doc_date">{{
          error_msg.doc_date
        }}</small>
      </div>

      <div class="p-field">
        <label for="">Tanggal Jatuh Tempo (opsional)</label>
        <InputText
          type="date"
          v-model="create_data.due_date"
          @input="is_error.due_date = false"
        />
        <small class="p-error" v-if="is_error.due_date">{{
          error_msg.due_date
        }}</small>
      </div>

      <div class="p-field">
        <label for="amount">Total Jenis Barang</label>
        <InputText
          type="number"
          locale="id-ID"
          v-model="create_data.total_item"
          placeholder="0"
          @input="is_error.total_item = false"
          @keydown="blockMinus"
        />
        <small class="p-error" v-if="is_error.total_item">{{
          error_msg.total_item
        }}</small>
      </div>
      <div class="p-field">
        <label for="amount">Total Qty</label>
        <InputText
          type="number"
          locale="id-ID"
          v-model="create_data.total_qty"
          placeholder="0"
          @input="is_error.total_qty = false"
          @keydown="blockMinus"
        />
        <small class="p-error" v-if="is_error.total_qty">{{
          error_msg.total_qty
        }}</small>
      </div>

      <div class="p-field">
        <label for="amount">Total Harga</label>
        <InputNumber
          locale="id-ID"
          v-model="create_data.total_price"
          placeholder="0"
          @input="is_error.total_price = false"
        />
        <small class="p-error" v-if="is_error.total_price">{{
          error_msg.total_price
        }}</small>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          class="p-button-outlined"
          @click="create_dialog = false"
        />

        <Button
          label="Save"
          class="btn btn-primary"
          @click="createInitialPurchase()"
        >
          Save
          <span v-if="loading_save">
            <i class="pi pi-spin pi-spinner" style="font-size: 12px"></i>
          </span>
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
let moment = require('moment')

export default {
  computed: {
    ...mapState(['user', 'isAuthenticated'])
  },
  watch: {
    user() {
      this.getAuthUser()
    }
  },
  data() {
    return {
      moment: moment,

      loading: {
        filter: false,
        suppliers: false
      },

      filter: {
        doc_number: '',
        doc_date: '',
        mutation_code: '',
        page: '',
        status: ''
      },

      create_dialog: false,
      delete_dialog: false,
      loading_save: false,
      warehouse_manager_dialog: false,
      initial_purchase_id: null,

      suppliers: [],
      autoFilteredValue: [],
      supplier_selected: null,
      warehouses: [],
      warehouse_selected: null,
      initial_purchase_orders: [],

      create_data: {
        warehouse_id: null,
        sup_id: 0,
        doc_number: '',
        doc_date: '',
        total_qty: null,
        total_item: null,
        total_price: null,
        due_date: ''
      },
      delete_data: {},

      is_error: {
        warehouse_id: false,
        sup_id: false,
        mutation_code: false,
        doc_number: false,
        doc_date: false,
        total_qty: false,
        total_item: false,
        total_price: false,
        due_date: false
      },
      error_msg: {
        warehouse_id: '',
        sup_id: '',
        mutation_code: '',
        doc_number: '',
        doc_date: '',
        total_qty: '',
        total_item: '',
        total_price: '',
        due_date: ''
      },

      limit: 10,
      total_records: 0,
      currentPage: 1
    }
  },
  mounted() {
    this.getAuthUser()
    this.getInitialPurchaseOrders()
    this.getWarehouses()
    // Initialize debounced search (don't load suppliers yet - lazy load on dropdown click)
    this.initDebouncedSearch()
  },
  beforeDestroy() {
    // Cleanup debounced function
    if (this.$_searchSuppliersDebounced) {
      this.$_searchSuppliersDebounced = null
    }
  },
  methods: {
    getAuthUser() {
      console.info(this.user)
    },

    async getWarehouses() {
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/warehouses`
        )
        if (response.status === 200) {
          this.warehouses = response.data.data.warehouses || []
        }
      } catch (error) {
        console.error('Error fetching warehouses:', error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Gagal mengambil data warehouse'
        this.showToast('error', 'Error', errorMessage)
        this.warehouses = []
      }
    },

    blockMinus(event) {
      if (event.key === '-') {
        event.preventDefault()
      }
    },

    // Debounce utility method
    debounce(fn, delay) {
      let timeout
      return function (...args) {
        clearTimeout(timeout)
        timeout = setTimeout(() => fn.apply(this, args), delay)
      }
    },

    async getSuppliers(searchQuery = '', limit = 20) {
      this.loading.suppliers = true
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/suppliers`,
          {
            params: {
              search: searchQuery,
              limit: limit
            }
          }
        )

        if (response.status === 200) {
          return response.data.data.suppliers.map(item => {
            return {
              alias: `${item.code} - ${item.name}`,
              code: item.code,
              id: item.id,
              name: item.name,
              parent: item.parent
            }
          })
        }
        return []
      } catch (error) {
        console.error('Error fetching suppliers:', error)
        this.showToast('error', 'Error', 'Failed to fetch suppliers')
        return []
      } finally {
        this.loading.suppliers = false
      }
    },

    // Load initial suppliers for dropdown (first load when dropdown clicked)
    async loadSuppliersForDropdown() {
      const suppliers = await this.getSuppliers('', 20)
      this.suppliers = suppliers
      this.autoFilteredValue = suppliers
    },

    // Actual search method that performs API call (called by debounced wrapper)
    async _searchSuppliers(event) {
      const query = event.query || ''

      // If empty query (dropdown clicked), always load initial suppliers
      // This ensures fresh data is always loaded when dropdown is opened
      if (!query) {
        await this.loadSuppliersForDropdown()
        return
      }

      // API call with search query
      const suppliers = await this.getSuppliers(query, 20)
      this.autoFilteredValue = suppliers
    },

    // Initialize debounced search function
    initDebouncedSearch() {
      if (!this.$_searchSuppliersDebounced) {
        this.$_searchSuppliersDebounced = this.debounce(
          this._searchSuppliers,
          500
        )
      }
    },

    // Wrapper that calls debounced version
    searchSupplier(event) {
      const query = event.query || ''

      // If empty query (dropdown clicked), execute immediately without debounce
      // This ensures dropdown opens quickly with fresh data
      if (!query) {
        this._searchSuppliers(event)
        return
      }

      // For typing/search, use debounce to reduce API calls
      if (!this.$_searchSuppliersDebounced) {
        this.initDebouncedSearch()
      }
      this.$_searchSuppliersDebounced(event)
    },
    async warehouseManagerEdit() {
      let id_initial = parseInt(this.initial_purchase_id)

      try {
        const response = await this.$http.put(
          `${process.env.VUE_APP_API_URL}/dashboard/initial-purchase-order/${id_initial}`
        )

        if (response.status === 200) {
          this.warehouse_manager_dialog = false
          const index = this.initial_purchase_orders.findIndex(
            warehouse => warehouse.id === id_initial
          )

          // Update the status property in the data array to 'Pending'
          if (index !== -1) {
            this.$set(this.initial_purchase_orders, index, {
              ...this.initial_purchase_orders[index],
              status: 0 // Set the new status value for 'Pending'
            })
          }
          this.showToast('success', 'Berhasil ubah pembelian')
        } else {
          this.showToast(
            'error',
            'Gagal ubah pembelian',
            'Gagal mengubah status pembelian'
          )
          this.warehouse_manager_dialog = false
        }
      } catch (error) {
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Terjadi kesalahan saat mengubah status pembelian'
        this.showToast('error', 'Gagal ubah pembelian', errorMessage)
        this.warehouse_manager_dialog = false
        console.error('Error updating initial purchase order:', error)
      }
    },

    warehouseManagerDialog(data) {
      this.initial_purchase_id = data.id
      this.warehouse_manager_dialog = true
    },

    async getInitialPurchaseOrders(event) {
      let status = this.filter.status ? this.filter.status.code : ''

      let sup_id = ''
      if (this.supplier_selected && this.supplier_selected.id) {
        sup_id = this.supplier_selected.id
      }

      let doc_date = this.filter.doc_date
        ? moment(this.filter.doc_date).format('YYYY-MM-DD')
        : ''

      let page = 1
      if (event) {
        page = event.page + 1
        this.currentPage = page
      }

      const offset = (page - 1) * this.limit

      // Build query parameters using URLSearchParams
      const params = new URLSearchParams({
        mutation_code: this.filter.mutation_code || '',
        sup_id: sup_id || '',
        doc_number: this.filter.doc_number || '',
        doc_date: doc_date || '',
        limit: this.limit,
        offset: offset,
        status: status || '',
        order_by: '-created_at'
      })

      this.loading.filter = true
      try {
        const response = await this.$http.get(
          `${
            process.env.VUE_APP_API_URL
          }/dashboard/initial-purchase-orders?${params.toString()}`
        )

        if (response.status === 200) {
          this.initial_purchase_orders =
            response.data.data.initial_purchase_orders
          this.total_records = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching initial purchase orders:', error)
        this.showToast(
          'error',
          'Error',
          'Gagal mengambil data pembelian. Silakan coba lagi.'
        )
      } finally {
        this.loading.filter = false
      }
    },

    checkStatus(status) {
      if (status == 1) {
        // return Confirmed Hijau
        return 'Confirmed'
      } else {
        // return Pending merah
        return 'Pending'
      }
    },

    async createInitialPurchase() {
      if (this.validateForm()) {
        this.create_data.total_item = parseInt(this.create_data.total_item)
        this.create_data.total_qty = parseInt(this.create_data.total_qty)
        this.create_data.warehouse_id = this.warehouse_selected.id
        this.create_data.sup_id = this.supplier_selected.id
        this.loading_save = true

        try {
          const response = await this.$http.post(
            `${process.env.VUE_APP_API_URL}/dashboard/initial-purchase-orders`,
            this.create_data
          )

          if (response.status === 200) {
            this.showToast('success', 'Berhasil tambah pembelian')
            this.resetCreateData()
            this.getInitialPurchaseOrders()
          } else {
            const errorMessage =
              (response.data && response.data.message) ||
              'Gagal menambahkan pembelian'
            this.showToast('error', 'Gagal tambah pembelian', errorMessage)
          }
        } catch (error) {
          console.error('Error creating initial purchase:', error)
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            'Terjadi kesalahan saat menambahkan pembelian'
          this.showToast('error', 'Gagal tambah pembelian', errorMessage, 3000)
        } finally {
          this.loading_save = false
        }
      } else {
        this.showToast('error', 'Gagal tambah pembelian')
      }
    },

    validateForm() {
      let valid = true

      if (!this.warehouse_selected || !this.warehouse_selected.id) {
        valid = false
        this.is_error.warehouse_id = true
        this.error_msg.warehouse_id = 'Pilih warehouse terlebih dahulu'
      }
      if (!this.supplier_selected || !this.supplier_selected.id) {
        valid = false
        this.is_error.sup_id = true
        this.error_msg.sup_id = 'Pilih supplier terlebih dahulu'
      }
      if (!this.create_data.doc_number) {
        valid = false
        this.is_error.doc_number = true
        this.error_msg.doc_number = 'Nomor bon tidak boleh kosong'
      }
      if (!this.create_data.doc_date) {
        valid = false
        this.is_error.doc_date = true
        this.error_msg.doc_date = 'Pilih tanggal terlebih dahulu'
      }
      if (!this.create_data.total_item) {
        valid = false
        this.is_error.total_item = true
        this.error_msg.total_item = 'Jumlah barang tidak boleh kosong'
      }
      if (!this.create_data.total_qty) {
        valid = false
        this.is_error.total_qty = true
        this.error_msg.total_qty = 'Total barang tidak boleh kosong'
      }
      if (!this.create_data.total_price) {
        valid = false
        this.is_error.total_price = true
        this.error_msg.total_price = 'Jumlah harga tidak boleh kosong'
      }
      if (
        this.create_data.due_date &&
        this.create_data.doc_date &&
        this.create_data.due_date < this.create_data.doc_date
      ) {
        valid = false
        this.is_error.due_date = true
        this.error_msg.due_date =
          'Tanggal jatuh tempo tidak boleh kurang dari tanggal bon'
      }

      return valid
    },

    resetCreateData() {
      this.create_dialog = false
      this.warehouse_selected = null
      this.supplier_selected = null
      this.create_data = {
        warehouse_id: null,
        sup_id: 0,
        doc_number: '',
        doc_date: '',
        total_qty: null,
        total_item: null,
        total_price: null,
        due_date: ''
      }
    },

    confirmDelete(purchase) {
      this.delete_data = purchase
      this.delete_dialog = true
    },

    async deleteInitialPurchaseOrders() {
      try {
        const response = await this.$http.delete(
          `${process.env.VUE_APP_API_URL}/dashboard/initial-purchase-orders/${this.delete_data.id}`
        )

        this.delete_dialog = false

        if (response.status === 200) {
          this.showToast('success', 'Berhasil hapus pembelian')
          this.getInitialPurchaseOrders()
        } else {
          const errorMessage =
            (response.data && response.data.message) ||
            'Gagal menghapus pembelian'
          this.showToast('error', 'Gagal hapus pembelian', errorMessage)
        }
      } catch (error) {
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Terjadi kesalahan saat menghapus pembelian'
        this.showToast('error', 'Gagal hapus pembelian', errorMessage)
        console.error('Error deleting initial purchase order:', error)
      }
    },

    /**
     * Truncate text to specified length and add ellipsis
     * @param {string} text - Text to truncate
     * @param {number} maxLength - Maximum length before truncation
     * @returns {string} Truncated text with ellipsis
     */
    truncateText(text, maxLength = 20) {
      if (!text) return '-'
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    async resetFilters() {
      this.filter.doc_number = ''
      this.filter.doc_date = ''
      this.filter.mutation_code = ''
      this.filter.status = ''
      this.supplier_selected = null

      // Reload suppliers for fresh dropdown data
      await this.loadSuppliersForDropdown()
      this.getInitialPurchaseOrders()
    }
  }
}
</script>

<style lang="scss" scoped>
.card {
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12);
  padding: 1.5rem;

  .title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 1.5rem;
  }
}

.warehouse-item {
  padding: 0.5rem;

  .warehouse-text {
    font-size: 0.875rem;
    color: #495057;
  }
}

.w-100 {
  width: 100%;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6c757d;

  i {
    color: #2196f3;
  }
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
}

.text-right {
  text-align: right;
}

.font-medium {
  font-weight: 500;
}

.mx-2 {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  min-width: 120px;
  white-space: nowrap;
}

:deep(.p-datatable) {
  position: relative;
  width: 100%;
  overflow-x: auto;

  .p-datatable-thead > tr > th {
    background-color: #f8f9fa;
    padding: 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #495057;
    position: sticky;
    top: 0;
    z-index: 0;
    text-align: center;
    border-bottom: 2px solid #dee2e6;
    white-space: nowrap;

    &:nth-child(1) {
      width: 2.5rem;
      min-width: 2.5rem;
    } /* No. */
    &:nth-child(2) {
      width: 12.5rem;
      min-width: 12.5rem;
    } /* Warehouse (only for MAIN_PURCHASING) */
    &:nth-child(3) {
      width: 10rem;
      min-width: 10rem;
    } /* Kode Mutasi */
    &:nth-child(4) {
      width: 7rem;
      min-width: 7rem;
    } /* Tanggal Bon */
    &:nth-child(5) {
      width: 13.5rem;
      min-width: 13.5rem;
    } /* No Bon */
    &:nth-child(6) {
      width: 12.5rem;
      min-width: 12.5rem;
    } /* Supplier */
    &:nth-child(7) {
      width: 7rem;
      min-width: 7rem;
    } /* Jatuh Tempo */
    &:nth-child(8) {
      width: 5rem;
      min-width: 5rem;
    } /* Status */
    &:nth-child(9) {
      width: 5rem;
      min-width: 5rem;
    } /* Total Barang */
    &:nth-child(10) {
      width: 8rem;
      min-width: 8rem;
    } /* Total Harga */
    &:nth-child(11) {
      width: 5rem;
      min-width: 5rem;
    } /* Aksi */
  }

  .p-datatable-tbody > tr > td {
    padding: 0.75rem;
    font-size: 0.875rem;
    background: #ffffff;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;

    &:nth-child(1),
    &:nth-child(3),
    &:nth-child(4),
    &:nth-child(5) {
      text-align: center;
    }

    &:nth-child(2),
    &:nth-child(6) {
      text-align: left;
    }

    &:nth-child(7),
    &:nth-child(8) {
      text-align: center;
    }

    &:nth-child(9),
    &:nth-child(10) {
      text-align: right;
    }

    &:nth-child(1) {
      width: 2.5rem;
      min-width: 2.5rem;
    } /* No. */
    &:nth-child(2) {
      width: 12.5rem;
      min-width: 12.5rem;
    } /* Warehouse (only for MAIN_PURCHASING) */
    &:nth-child(3) {
      width: 10rem;
      min-width: 10rem;
    } /* Kode Mutasi */
    &:nth-child(4) {
      width: 7rem;
      min-width: 7rem;
    } /* Tanggal Bon */
    &:nth-child(5) {
      width: 13.5rem;
      min-width: 13.5rem;
    } /* No Bon */
    &:nth-child(6) {
      width: 12.5rem;
      min-width: 12.5rem;
    } /* Supplier */
    &:nth-child(7) {
      width: 7rem;
      min-width: 7rem;
    } /* Jatuh Tempo */
    &:nth-child(8) {
      width: 5rem;
      min-width: 5rem;
    } /* Status */
    &:nth-child(9) {
      width: 5rem;
      min-width: 5rem;
    } /* Total Barang */
    &:nth-child(10) {
      width: 8rem;
      min-width: 8rem;
    } /* Total Harga */
    &:nth-child(11) {
      width: 5rem;
      min-width: 5rem;
    } /* Aksi */
  }

  .p-datatable-tbody > tr:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  .p-datatable-tbody > tr.p-highlight {
    background-color: #e3f2fd;
    color: #1976d2;

    .font-medium {
      color: #1976d2;
    }

    .text-truncate {
      color: #1976d2;
    }

    .text-right {
      color: #1976d2;
    }
  }
}

.confirmation-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  i {
    color: #f44336;
    margin-right: 1rem;
  }
}

.text-success {
  color: #28a745;
}

.text-warning {
  color: #ffc107;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  min-width: 6rem;

  &.status-success {
    background-color: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #a5d6a7;
  }

  &.status-warning {
    background-color: #fff3e0;
    color: #e65100;
    border: 1px solid #ffcc80;
  }
}
</style>
